import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { callApi, HttpMethod } from "src/services/apiService";
import { toast } from "sonner";
import Loading from "src/components/Loading";
import {
  Grid,
  GridColumn as Column,
  GridCellProps,
  GridSortChangeEvent,
  GridFilterChangeEvent,
} from "@progress/kendo-react-grid";
import { SortDescriptor, orderBy, CompositeFilterDescriptor, filterBy } from "@progress/kendo-data-query";
import GoHighLevelNav from "./GoHighLevelNav";
import styles from "./GoHighLevel.module.css";

interface LocationRow {
  agencyName: string;
  locationName: string;
  ghlLocationId: string;
  isActive: boolean;
  inaccessibleSince: string | null;
  contactCount: number | null;
  contactCountUpdatedAt: string | null;
  bulkScrubEnabled: boolean;
  bulkScrubSchedule: string | null;
  bulkScrubTimeZone: string | null;
  rescrubIntervalHours: number | null;
  bulkScrubLastRunAt: string | null;
  scrubLimit: number | null;
}

const Locations: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState<LocationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState<SortDescriptor[]>([{ field: "contactCount", dir: "desc" }]);
  const [gridFilter, setGridFilter] = useState<CompositeFilterDescriptor | undefined>(
    searchParams.get("bulkScrub") === "1"
      ? { logic: "and", filters: [{ field: "bulkScrubEnabled", operator: "eq", value: true }] }
      : undefined,
  );

  useEffect(() => {
    (async () => {
      try {
        setRows(await callApi("sysadmin/gohighlevel/locations", HttpMethod.GET));
      } catch {
        toast.error("Failed to load locations.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const data = useMemo(
    () => (gridFilter ? filterBy(orderBy(rows, sort), gridFilter) : orderBy(rows, sort)),
    [rows, sort, gridFilter],
  );

  const totalContacts = useMemo(
    () => rows.reduce((sum, r) => sum + (r.contactCount ?? 0), 0),
    [rows],
  );

  const ContactsCell = (props: GridCellProps) => {
    const v = (props.dataItem as LocationRow).contactCount;
    return <td style={{ textAlign: "right" }}>{v != null ? v.toLocaleString() : "—"}</td>;
  };

  const ScrubLimitCell = (props: GridCellProps) => {
    const v = (props.dataItem as LocationRow).scrubLimit;
    return <td style={{ textAlign: "right" }}>{v != null ? v.toLocaleString() : "—"}</td>;
  };

  // GHL still reports the app as installed in subaccounts it has paused or deleted, so a refused
  // location token is the only signal we get that one has gone away.
  const AccessCell = (props: GridCellProps) => {
    const since = (props.dataItem as LocationRow).inaccessibleSince;
    return (
      <td>
        {since ? (
          <span className="badge bg-danger" title={`GoHighLevel refused access since ${new Date(since).toLocaleString()}`}>
            No access
          </span>
        ) : (
          <span className="badge bg-success">OK</span>
        )}
      </td>
    );
  };

  const BulkScrubCell = (props: GridCellProps) => {
    const on = (props.dataItem as LocationRow).bulkScrubEnabled;
    return (
      <td>
        <span className={`badge ${on ? "bg-success" : "bg-secondary"}`}>{on ? "On" : "Off"}</span>
      </td>
    );
  };

  const RefreshedCell = (props: GridCellProps) => {
    const v = (props.dataItem as LocationRow).contactCountUpdatedAt;
    return <td>{v ? new Date(v).toLocaleString() : "Never"}</td>;
  };

  const LastRunCell = (props: GridCellProps) => {
    const v = (props.dataItem as LocationRow).bulkScrubLastRunAt;
    return <td>{v ? new Date(v).toLocaleString() : "Never"}</td>;
  };

  const ActionCell = (props: GridCellProps) => (
    <td>
      <button
        className={styles.linkButton}
        onClick={() => navigate(`/sysadmin/gohighlevel/locations/${(props.dataItem as LocationRow).ghlLocationId}`)}
      >
        View detail
      </button>
    </td>
  );

  if (loading) return <Loading />;

  return (
    <div className={styles.container}>
      <GoHighLevelNav />
      <div className={styles.pageHeader}>
        <div>
          <h2 className={styles.pageTitle}>All Locations</h2>
          <p className={styles.pageSubtitle}>
            {rows.length} locations · {totalContacts.toLocaleString()} total contacts
          </p>
        </div>
      </div>

      <Grid
        className="sysadmin-grid"
        data={data}
        dataItemKey="ghlLocationId"
        sortable={true}
        resizable={true}
        sort={sort}
        onSortChange={(e: GridSortChangeEvent) => setSort(e.sort)}
        filterable={true}
        filter={gridFilter}
        onFilterChange={(e: GridFilterChangeEvent) => setGridFilter(e.filter)}
        style={{ height: "calc(100vh - 190px)", width: "100%" }}
      >
        <Column field="agencyName" title="Agency" width={220} />
        <Column field="locationName" title="Location" width={240} />
        <Column field="inaccessibleSince" title="Access" width={120} filterable={false} cells={{ data: AccessCell }} />
        <Column field="contactCount" title="Contacts" width={130} filterable={false} cells={{ data: ContactsCell }} />
        <Column field="scrubLimit" title="Scrubs Included in Plan" width={130} filterable={false} cells={{ data: ScrubLimitCell }} />
        <Column field="contactCountUpdatedAt" title="Count Refreshed" width={190} filterable={false} cells={{ data: RefreshedCell }} />
        <Column field="bulkScrubEnabled" title="Bulk Scrub" width={130} filterable={false} cells={{ data: BulkScrubCell }} />
        <Column field="bulkScrubSchedule" title="Schedule" width={130} />
        <Column field="bulkScrubLastRunAt" title="Last Run" width={190} filterable={false} cells={{ data: LastRunCell }} />
        <Column title="Actions" width={130} filterable={false} sortable={false} cells={{ data: ActionCell }} />
      </Grid>
    </div>
  );
};

export default Locations;
